import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, formatNumber } from '../lib/format.js';
import { billingQuery } from '../lib/queries.js';
import { magToast } from './toaster.js';

// [s264] Mag-tokens balance chip — always-visible counter in the top
// nav so the founder knows where they stand without opening settings.
// Clicks through to /settings/billing for top-up.
//
// State buckets drive color + copy:
//   - empty (≤ 0):   pulse red, "out of tokens", links to settings
//   - on the house:  plum/gift, "N tokens · on the house" — org is
//                    still on its signup trial grant (never subscribed)
//                    with tokens left; these are free, so we frame them
//                    as a gift and skip the amber "running low" nudge
//   - low (< 200):   warm amber, "running low"
//   - normal:        muted ink, just shows the count
//
// Updates animate when the balance changes (matches the project's
// microanimations-on-everything rule):
//   - the number count-up-rolls from old → new (RAF tween, eased)
//   - a "+N / −N" delta floats up + fades above the pill
//   - the pill gives a quick scale bump
// All three respect prefers-reduced-motion (snap, no float, no bump).
//
// Freshness: the balance is mostly burned by background queues (not
// foreground clicks), so we poll on a gentle ~20s cadence — but only
// while the tab is visible (refetchIntervalInBackground stays false) so
// idle tabs cost nothing. Without this the counter would only move on
// window-focus and the change animation would rarely fire.

const POLL_MS = 20_000;
const COUNT_UP_MS = 650;
const BUMP_MS = 450;
const DELTA_RISE_MS = 900;

export function TokenBalanceChip() {
  const { data } = useQuery({
    ...billingQuery(),
    refetchInterval: POLL_MS,
    // Default false — pause polling when the tab is hidden.
    refetchIntervalInBackground: false,
  });
  const balance = data?.magTokens.balance ?? null;
  const cycle = data?.magTokens.cycle ?? null;
  const periodEndIso = data?.currentPeriodEnd ?? null;

  const reduceMotion = usePrefersReducedMotion();
  // Count-up display value — rolls toward `balance` over COUNT_UP_MS.
  const display = useCountUp(balance, COUNT_UP_MS, reduceMotion);

  // Change detection drives the bump, the floating delta, and the
  // top-up toast. Tracks the previous settled balance; guards the
  // first-load case (prevRef null) so we don't fire a phantom change.
  const prevRef = useRef<number | null>(null);
  const [bumped, setBumped] = useState(false);
  const [delta, setDelta] = useState<{ id: number; value: number } | null>(null);
  useEffect(() => {
    if (balance === null) return undefined;
    const prev = prevRef.current;
    prevRef.current = balance;
    if (prev === null || prev === balance) return undefined;

    const diff = balance - prev;
    setBumped(true);
    // id keys the floater so a rapid second change restarts the rise
    // animation instead of letting React reuse the in-flight element.
    setDelta({ id: prev * 1_000_003 + balance, value: diff });
    // Big positive jumps (subscription grant / top-up / autobuy) also
    // toast — immediate confirmation without watching the chip. Small
    // llm-refund decrements stay quiet.
    if (diff >= 50) {
      magToast.success(
        `+${formatNumber(diff)} mag tokens`,
        `balance now ${formatNumber(balance)}.`,
      );
    }
    const bt = setTimeout(() => setBumped(false), BUMP_MS);
    const dt = setTimeout(() => setDelta(null), DELTA_RISE_MS);
    return () => {
      clearTimeout(bt);
      clearTimeout(dt);
    };
  }, [balance]);

  if (balance === null) {
    // Pre-fetch state — render a stable-width skeleton so the chip
    // doesn't pop into existence and shift the rest of the nav.
    return (
      <div
        aria-hidden="true"
        className="hidden h-7 w-[88px] animate-pulse rounded-full bg-[var(--line)]/40 sm:block"
      />
    );
  }

  const empty = balance <= 0;
  // "On the house": the org is still on its signup trial grant (never
  // subscribed) with tokens left. These are free tokens, so frame them
  // as a gift and suppress the amber "running low" warning — that nudge
  // is for paying users about to run out, not a founder spending down a
  // trial they haven't paid for.
  const freeTrial = !empty && data?.subscriptionStatus === 'none';
  // [task 6b9cf44] THE SERVER OWNS THIS NUMBER. It is the threshold the
  // low-balance email fires at, so reading it here makes the amber chip and
  // the inbox agree by construction instead of by two constants matching.
  //
  // Absent means an api older than this change — the dashboard needs a manual
  // Pages deploy while the api ships on push, so in practice the field is
  // always there by the time this code is. It deliberately does NOT fall back
  // to a hardcoded number: that is the duplication this removed. No threshold
  // means no amber state, and `empty` and `freeTrial` still work — a missing
  // warning for one deploy window is a smaller lie than a warning at a
  // threshold nobody authorised.
  const lowThreshold = data?.magTokens.lowBalanceThresholdTokens ?? null;
  const low = !empty && !freeTrial && lowThreshold !== null && balance < lowThreshold;
  // Animate the digits via the count-up value; fall back to the real
  // balance if the tween hasn't produced a value yet.
  const shown = display ?? balance;

  const baseCls =
    'group hidden sm:inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11.5px] font-semibold no-underline transition-transform duration-200 ease-out hover:-translate-y-px';
  const stateCls = empty
    ? 'border-[var(--pulse)] bg-[var(--pulse)]/10 text-[var(--pulse)]'
    : freeTrial
      ? 'border-[var(--plum)]/35 bg-[var(--spell-soft)]/50 text-[var(--plum)]'
      : low
        ? 'border-[#d4a017] bg-[#d4a017]/8 text-[#7a5a05]'
        : 'border-[var(--line)] bg-white text-[var(--ink)]/80 hover:text-[var(--ink)]';
  const bumpCls = bumped && !reduceMotion ? 'scale-[1.06]' : 'scale-100';

  const unit = balance === 1 ? 'token' : 'tokens';
  const label = empty
    ? 'out of tokens'
    : freeTrial
      ? `${formatNumber(shown)} ${unit} · on the house`
      : `${formatNumber(shown)} ${unit}`;

  // [s264] Burn-down hint for the tooltip. Three pieces:
  //   1. "renews <date>" — always, when we know the period end
  //   2. "~N days at current rate" — when we have rate signal
  //   3. State-prefixed copy (out / low / normal)
  //
  // Rate = consumed / days_elapsed. Projection = balance / rate.
  // Skip projection when consumption is too small to be meaningful
  // (< 5 tokens / day) — at that rate the founder won't run out
  // before renewal anyway, and the projection would be noisy.
  const title = buildTooltip({
    balance,
    empty,
    freeTrial,
    low,
    cycle,
    periodEndIso,
  });

  return (
    <span className="relative hidden sm:inline-flex">
      {delta && !reduceMotion ? (
        <span
          key={delta.id}
          aria-hidden="true"
          className={`mag-token-rise pointer-events-none absolute -top-2.5 right-2 z-10 font-mono text-[11px] font-bold tabular-nums ${
            delta.value > 0 ? 'text-[var(--sage)]' : 'text-[var(--ink)]/45'
          }`}
        >
          {delta.value > 0 ? '+' : '−'}
          {formatNumber(Math.abs(delta.value))}
        </span>
      ) : null}
      <Link to="/settings/billing" className={`${baseCls} ${stateCls} ${bumpCls}`} title={title}>
        <span
          aria-hidden="true"
          className={`inline-block h-1.5 w-1.5 rounded-full ${
            empty
              ? 'animate-pulse bg-[var(--pulse)]'
              : freeTrial
                ? 'bg-[var(--plum)]'
                : low
                  ? 'bg-[#d4a017]'
                  : 'bg-[var(--plum)]/70'
          }`}
        />
        <span className="font-mono tabular-nums">{label}</span>
      </Link>
    </span>
  );
}

/** Roll a displayed integer toward `target` over `durationMs` with an
 *  easeOutCubic curve (RAF). Snaps immediately on first value, when the
 *  value is unchanged, or under reduced-motion. Captures the live
 *  displayed value at the moment the target changes so a mid-flight
 *  change re-tweens smoothly instead of jumping. */
function useCountUp(target: number | null, durationMs: number, reduce: boolean): number | null {
  const [display, setDisplay] = useState<number | null>(target);
  const displayRef = useRef<number | null>(target);
  const rafRef = useRef<number | null>(null);

  // Stable so it can sit in the effect deps without re-triggering the
  // tween every render.
  const set = useCallback((v: number | null) => {
    displayRef.current = v;
    setDisplay(v);
  }, []);

  useEffect(() => {
    if (target === null) {
      set(null);
      return undefined;
    }
    const from = displayRef.current;
    if (from === null || from === target || reduce) {
      set(target);
      return undefined;
    }
    const start = performance.now();
    const diff = target - from;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - (1 - t) ** 3;
      set(Math.round(from + diff * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        set(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // displayRef is intentionally read (not in deps) so an interrupted
    // tween resumes from the live displayed value.
  }, [target, durationMs, reduce, set]);

  return display;
}

/** Track the user's prefers-reduced-motion setting reactively. */
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(mq.matches);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

function buildTooltip(input: {
  balance: number;
  empty: boolean;
  freeTrial: boolean;
  low: boolean;
  cycle: { startsAt: string; consumedTokens: number } | null;
  periodEndIso: string | null;
}): string {
  const lines: string[] = [];

  if (input.empty) {
    lines.push('mag is paused on generative work — top up to bring her back online');
  } else if (input.freeTrial) {
    lines.push('your free tokens to try mag — no card needed yet');
  } else if (input.low) {
    lines.push('running low — top up before mag pauses');
  } else {
    lines.push('mag tokens — click to top up');
  }

  // Renewal date — surface in friendly form when present. NOT shown
  // for orgs without an active subscription (currentPeriodEnd null).
  if (input.periodEndIso) {
    const renewsAt = new Date(input.periodEndIso);
    const renewsLabel = formatDate(renewsAt, {
      month: 'short',
      day: 'numeric',
    });
    lines.push(`renews ${renewsLabel}`);
  }

  // Burn-down projection. Need cycle data + meaningful consumption.
  if (input.cycle && !input.empty && input.cycle.consumedTokens > 0) {
    const cycleStart = new Date(input.cycle.startsAt);
    const daysElapsed = Math.max(0.25, (Date.now() - cycleStart.getTime()) / (24 * 60 * 60 * 1000));
    const dailyRate = input.cycle.consumedTokens / daysElapsed;
    if (dailyRate >= 5) {
      // Meaningful consumption — at >=5 tokens/day a projection
      // changes the founder's behavior. Below that it's just noise.
      const daysRemaining = Math.floor(input.balance / dailyRate);
      lines.push(`~${daysRemaining} day${daysRemaining === 1 ? '' : 's'} at current rate`);
    }
  }

  return lines.join(' · ');
}
